import * as api from "./client";

// ============================================================================
// Type Definitions
// ============================================================================

export type NameTranslation = {
  localeCode: string;
  translatedName: string;
};

export type Company = {
  id: string;
  companyId: number;
  name: string;
  originalName: string | null;
  globalName: string | null;
  businessType: string | null;
  businessTypeId: number | null;
  firmRef: string | null;
  slug: string | null;
  crmEntityNumber: string | null;
  website: string | null;
  industry: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  translations?: NameTranslation[];
};

export type CompanyCreateRequest = {
  name: string;
  originalName?: string | null;
  globalName?: string | null;
  businessType?: string | null;
  slug?: string | null;
  website?: string | null;
  industry?: string | null;
  notes?: string | null;
  crmEntityNumber?: string | null;
  translations?: NameTranslation[];
};

export type CompanyUpdateRequest = {
  name?: string;
  originalName?: string | null;
  globalName?: string | null;
  businessType?: string | null;
  slug?: string | null;
  website?: string | null;
  industry?: string | null;
  notes?: string | null;
  translations?: NameTranslation[];
  removeTranslations?: string[];
};

export type ContactTranslation = NameTranslation;

export type Contact = {
  id: string;
  contactId: number;
  identityId: string;
  name: string;
  slug: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  jobTitle: string | null;
  companyId: string | null;
  officeId: string | null;
  officeName: string | null;
  countryName: string | null;
  createdAt: string;
  updatedAt: string;
  translations: ContactTranslation[];
};

export type ContactCreateRequest = {
  firstName: string;
  lastName: string;
  name?: string;
  slug?: string;
  email?: string | null;
  jobTitle?: string | null;
  companyId?: string | null;
  officeId?: string | null;
  translations?: ContactTranslation[];
};

export type ContactUpdateRequest = {
  firstName?: string;
  lastName?: string;
  name?: string | null;
  slug?: string | null;
  email?: string | null;
  jobTitle?: string | null;
  companyId?: string | null;
  officeId?: string | null;
  translations?: ContactTranslation[];
  removeTranslations?: string[];
};

export type Office = {
  id: string;
  officeId: number;
  companyId: string | null;
  companyName: string | null;
  companySlug: string | null;
  isHeadOffice: boolean | null;
  firmRef: string | null;
  officeRef: string | null;
  name: string;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  city: string | null;
  county: string | null;
  postcode: string | null;
  countryName: string | null;
  countryTermSlug: string | null;
  countrySlug: string | null;
  countryParentName: string | null;
  countryParentSlug: string | null;
  countryId: number | null;
  location: string | null;
  phone: string | null;
  fax: string | null;
  email: string | null;
  websiteUrl: string | null;
  slug: string | null;
  crmEntityNumber: string | null;
  createdAt: string;
  updatedAt: string;
  translations?: NameTranslation[];
};

export type OfficeCreateRequest = {
  name: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  city?: string | null;
  county?: string | null;
  postcode?: string | null;
  countryName?: string | null;
  countryTermSlug?: string | null;
  countrySlug?: string | null;
  countryParentName?: string | null;
  countryParentSlug?: string | null;
  countryId?: number | null;
  location?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  websiteUrl?: string | null;
  slug?: string | null;
  crmEntityNumber?: string | null;
  isHeadOffice?: boolean | null;
  firmRef?: string | null;
  officeRef?: string | null;
  translations?: NameTranslation[];
};

export type OfficeUpdateRequest = {
  companyId?: string | null;
  name?: string;
  addressLine1?: string | null;
  addressLine2?: string | null;
  addressLine3?: string | null;
  city?: string | null;
  county?: string | null;
  postcode?: string | null;
  countryName?: string | null;
  countryTermSlug?: string | null;
  countrySlug?: string | null;
  countryParentName?: string | null;
  countryParentSlug?: string | null;
  countryId?: number | null;
  location?: string | null;
  phone?: string | null;
  fax?: string | null;
  email?: string | null;
  websiteUrl?: string | null;
  slug?: string | null;
  crmEntityNumber?: string | null;
  isHeadOffice?: boolean | null;
  firmRef?: string | null;
  officeRef?: string | null;
  translations?: NameTranslation[];
  removeTranslations?: string[];
};

export type MergeCompanyResult = {
  relationship: {
    id: number;
    sourceEntityType: string;
    sourceEntityId: string;
    targetEntityType: string;
    targetEntityId: string;
    relationshipType: string;
    effectiveDate: string | null;
    createdBy: string | null;
    createdAt: string;
  };
  officesTransferred: number;
  contactsUpdated: number;
};

export type DemergeCompanyResult = {
  newCompanies: Array<{ id: string; companyId: number; name: string }>;
  relationships: Array<{ id: number; relationshipType: string }>;
  officesTransferred: number;
};

export type OfficeCountry = {
  id: number;
  name: string;
};

export type PaginatedResponse<T> = {
  data: T[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore?: boolean;
  };
  requestId: string | null;
};

export type SingleResponse<T> = {
  data: T;
  requestId: string | null;
};

// ============================================================================
// Companies Service
// ============================================================================

export const companies = {
  getAll: async (filters?: {
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Company>> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("name", filters.search);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      companies: Company[];
      pagination: { total: number; limit: number; offset: number };
      requestId: string | null;
    }>(`/v1/address-book/companies${query}`);
    const p = response.pagination;
    return {
      data: response.companies,
      pagination: { ...p, hasMore: p.offset + p.limit < p.total },
      requestId: response.requestId,
    };
  },

  getById: async (id: string): Promise<SingleResponse<Company>> => {
    const response = await api.get<{ company: Company; requestId: string | null }>(
      `/v1/address-book/companies/${id}`,
    );
    return { data: response.company, requestId: response.requestId };
  },

  /**
   * Resolves an account firm ref to its Address Book company. Returns null on 404
   * (firm not in the Address Book yet) so callers can show a fallback rather than fail.
   */
  getByFirmRef: async (firmRef: string): Promise<Company | null> => {
    try {
      const response = await api.get<{ company: Company; requestId: string | null }>(
        `/v1/address-book/companies/by-firm-ref/${encodeURIComponent(firmRef)}`,
      );
      return response.company;
    } catch (err) {
      if ((err as { status?: number })?.status === 404) return null;
      throw err;
    }
  },

  create: async (data: CompanyCreateRequest): Promise<SingleResponse<Company>> => {
    const response = await api.post<{ company: Company; requestId: string | null }>(
      "/v1/address-book/companies",
      data,
    );
    return { data: response.company, requestId: response.requestId };
  },

  update: async (id: string, data: CompanyUpdateRequest): Promise<SingleResponse<Company>> => {
    const response = await api.put<{ company: Company; requestId: string | null }>(
      `/v1/address-book/companies/${id}`,
      data,
    );
    return { data: response.company, requestId: response.requestId };
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/address-book/companies/${id}`);
  },

  merge: async (
    id: string,
    data: { targetCompanyId: string; effectiveDate?: string | null },
  ): Promise<{ result: MergeCompanyResult; requestId: string | null }> => {
    return api.post(`/v1/address-book/companies/${id}/merge`, data);
  },

  demerge: async (
    id: string,
    data: {
      newCompanies: Array<{
        name: string;
        originalName?: string | null;
        globalName?: string | null;
        businessType?: string | null;
        slug?: string | null;
        industry?: string | null;
        website?: string | null;
        notes?: string | null;
        officeIds: string[];
        contactIds?: string[];
      }>;
      effectiveDate?: string | null;
    },
  ): Promise<{ result: DemergeCompanyResult; requestId: string | null }> => {
    return api.post(`/v1/address-book/companies/${id}/demerge`, data);
  },
};

// ============================================================================
// Contacts Service
// ============================================================================

export const contacts = {
  getAll: async (filters?: {
    search?: string;
    companyId?: string;
    officeId?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Contact>> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("name", filters.search);
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.officeId) params.append("officeId", filters.officeId);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      contacts: Contact[];
      pagination: { total: number; limit: number; offset: number };
      requestId: string | null;
    }>(`/v1/address-book/contacts${query}`);
    const p = response.pagination;
    return {
      data: response.contacts,
      pagination: { ...p, hasMore: p.offset + p.limit < p.total },
      requestId: response.requestId,
    };
  },

  getById: async (id: string): Promise<SingleResponse<Contact>> => {
    const response = await api.get<{ contact: Contact; requestId: string | null }>(
      `/v1/address-book/contacts/${id}`,
    );
    return { data: response.contact, requestId: response.requestId };
  },

  create: async (data: ContactCreateRequest): Promise<SingleResponse<Contact>> => {
    const response = await api.post<{ contact: Contact; requestId: string | null }>(
      "/v1/address-book/contacts",
      data,
    );
    return { data: response.contact, requestId: response.requestId };
  },

  update: async (id: string, data: ContactUpdateRequest): Promise<SingleResponse<Contact>> => {
    const response = await api.put<{ contact: Contact; requestId: string | null }>(
      `/v1/address-book/contacts/${id}`,
      data,
    );
    return { data: response.contact, requestId: response.requestId };
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/address-book/contacts/${id}`);
  },

  merge: async (id: string, data: MergeContactRequest): Promise<MergeContactResponse> => {
    return api.post<MergeContactResponse>(`/v1/address-book/contacts/${id}/merge`, data);
  },

  getMergeHistory: async (id: string) => {
    return api.get<{
      merges: Array<{
        relationship: Record<string, unknown>;
        sourceContact: Contact;
      }>;
      requestId: string;
    }>(`/v1/address-book/contacts/${id}/merge-history`);
  },

  demerge: async (id: string, data: UndoMergeRequest): Promise<UndoMergeResponse> => {
    return api.post<UndoMergeResponse>(`/v1/address-book/contacts/${id}/demerge`, data);
  },

  split: async (id: string, data: SplitContactRequest): Promise<SplitContactResponse> => {
    return api.post<SplitContactResponse>(`/v1/address-book/contacts/${id}/split`, data);
  },
};

export type MergeContactRequest = {
  targetContactId: string;
  effectiveDate?: string | null;
};

export type MergeContactResponse = {
  relationship: Record<string, unknown>;
  rolesTransferred: number;
  requestId: string;
};

export type UndoMergeRequest = {
  mergeRelationshipId: number;
  roleIds: string[];
  effectiveDate?: string | null;
};

export type UndoMergeResponse = {
  restoredContact: Contact;
  relationship: Record<string, unknown>;
  rolesMoved: number;
  requestId: string;
};

export type SplitContactRequest = {
  newContact: {
    firstName: string;
    lastName: string;
    name?: string;
    slug?: string;
    email?: string;
    jobTitle?: string;
  };
  roleIds: string[];
  effectiveDate?: string | null;
};

export type SplitContactResponse = {
  newContact: Contact;
  relationship: Record<string, unknown>;
  rolesMoved: number;
  requestId: string;
};

// ============================================================================
// Contact Roles Types
// ============================================================================

export type ContactRole = {
  id: string;
  contactId: string;
  officeId: string;
  roleType: string | null;
  jobTitleOverride: string | null;
  emailOverride: string | null;
  isPortalContact: boolean | null;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ContactRoleCreateRequest = {
  contactId: string;
  officeId: string;
  roleType?: string | null;
  jobTitleOverride?: string | null;
  emailOverride?: string | null;
  isPortalContact?: boolean;
  startDate?: string;
  endDate?: string;
};

export type ContactRoleUpdateRequest = {
  officeId?: string;
  roleType?: string | null;
  jobTitleOverride?: string | null;
  emailOverride?: string | null;
  isPortalContact?: boolean;
  startDate?: string;
  endDate?: string | null;
};

// ============================================================================
// Offices Service
// ============================================================================

export const offices = {
  getAll: async (filters?: {
    search?: string;
    companyId?: string;
    limit?: number;
    offset?: number;
  }): Promise<PaginatedResponse<Office>> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("name", filters.search);
    if (filters?.companyId) params.append("companyId", filters.companyId);
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      offices: Office[];
      pagination: { total: number; limit: number; offset: number };
      requestId: string | null;
    }>(`/v1/address-book/offices${query}`);
    const p = response.pagination;
    return {
      data: response.offices,
      pagination: { ...p, hasMore: p.offset + p.limit < p.total },
      requestId: response.requestId,
    };
  },

  getById: async (id: string): Promise<SingleResponse<Office>> => {
    const response = await api.get<{ office: Office; requestId: string | null }>(
      `/v1/address-book/offices/${id}`,
    );
    return { data: response.office, requestId: response.requestId };
  },

  create: async (companyId: string, data: OfficeCreateRequest): Promise<SingleResponse<Office>> => {
    const response = await api.post<{ office: Office; requestId: string | null }>(
      `/v1/address-book/companies/${companyId}/offices`,
      data,
    );
    return { data: response.office, requestId: response.requestId };
  },

  update: async (id: string, data: OfficeUpdateRequest): Promise<SingleResponse<Office>> => {
    const response = await api.put<{ office: Office; requestId: string | null }>(
      `/v1/address-book/offices/${id}`,
      data,
    );
    return { data: response.office, requestId: response.requestId };
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/address-book/offices/${id}`);
  },

  getCountries: async (): Promise<OfficeCountry[]> => {
    const response = await api.get<{
      countries: OfficeCountry[];
      pagination: PaginatedResponse<OfficeCountry>["pagination"];
      requestId: string | null;
    }>("/v1/address-book/offices/countries");
    return response.countries;
  },

  merge: async (id: string, data: MergeOfficeRequest): Promise<MergeOfficeResponse> => {
    return api.post<MergeOfficeResponse>(`/v1/address-book/offices/${id}/merge`, data);
  },

  getMergeHistory: async (id: string): Promise<OfficeMergeHistoryResponse> => {
    return api.get<OfficeMergeHistoryResponse>(`/v1/address-book/offices/${id}/merge-history`);
  },

  demerge: async (id: string, data: DemergeOfficeRequest): Promise<DemergeOfficeResponse> => {
    return api.post<DemergeOfficeResponse>(`/v1/address-book/offices/${id}/demerge`, data);
  },
};

export type ExternalReference = {
  id: string;
  entityType: string;
  entityId: string;
  referenceType: string;
  referenceValue: string;
  isCanonical: boolean;
  createdAt: string;
  updatedAt: string;
};

export type OfficeMergeRelationship = {
  id: number;
  sourceEntityType: string;
  sourceEntityId: string;
  targetEntityType: string;
  targetEntityId: string;
  relationshipType: string;
  effectiveDate: string | null;
  createdBy: string | null;
  createdAt: string;
};

export type MergeOfficeRequest = {
  targetOfficeId: string;
  effectiveDate?: string | null;
};

export type MergeOfficeResponse = {
  relationship: OfficeMergeRelationship;
  rolesMoved: number;
  requestId: string;
};

export type OfficeMergeHistoryEntry = {
  relationship: OfficeMergeRelationship;
  sourceOffice: { id: string; officeId: number; name: string };
};

export type OfficeMergeHistoryResponse = {
  merges: OfficeMergeHistoryEntry[];
  requestId: string;
};

export type DemergeOfficeRequest = {
  mergeRelationshipId: number;
  effectiveDate?: string | null;
};

export type DemergeOfficeResponse = {
  restoredOffice: { id: string; officeId: number; name: string };
  relationship: Record<string, unknown>;
  rolesMoved: number;
  requestId: string;
};

// ============================================================================
// Contact Roles Service
// ============================================================================

export type ContactRoleFilters = {
  contactId?: string;
  officeId?: string;
  activeOnly?: boolean;
  limit?: number;
  offset?: number;
};

export const contactRoles = {
  getAll: async (filters?: ContactRoleFilters): Promise<PaginatedResponse<ContactRole>> => {
    const params = new URLSearchParams();
    if (filters?.contactId) params.append("contactId", filters.contactId);
    if (filters?.officeId) params.append("officeId", filters.officeId);
    if (filters?.activeOnly !== undefined) params.append("activeOnly", String(filters.activeOnly));
    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get<{
      contactRoles: ContactRole[];
      pagination: { total: number; limit: number; offset: number };
      requestId: string | null;
    }>(`/v1/address-book/contact-roles${query}`);
    const p = response.pagination;
    return {
      data: response.contactRoles,
      pagination: { ...p, hasMore: p.offset + p.limit < p.total },
      requestId: response.requestId,
    };
  },

  getById: async (id: string): Promise<SingleResponse<ContactRole>> => {
    const response = await api.get<{ contactRole: ContactRole; requestId: string | null }>(
      `/v1/address-book/contact-roles/${id}`,
    );
    return { data: response.contactRole, requestId: response.requestId };
  },

  create: async (data: ContactRoleCreateRequest): Promise<SingleResponse<ContactRole>> => {
    const response = await api.post<{ contactRole: ContactRole; requestId: string | null }>(
      "/v1/address-book/contact-roles",
      data,
    );
    return { data: response.contactRole, requestId: response.requestId };
  },

  update: async (
    id: string,
    data: ContactRoleUpdateRequest,
  ): Promise<SingleResponse<ContactRole>> => {
    const response = await api.put<{ contactRole: ContactRole; requestId: string | null }>(
      `/v1/address-book/contact-roles/${id}`,
      data,
    );
    return { data: response.contactRole, requestId: response.requestId };
  },

  delete: async (id: string): Promise<void> => {
    await api.del(`/v1/address-book/contact-roles/${id}`);
  },
};

// ============================================================================
// Relationships Types
// ============================================================================

export type FirmRelationshipType = "member_of" | "affiliated_with";

export type FirmRelationshipOtherFirm = {
  id: string;
  name: string;
  business_type: string | null;
};

export type FirmRelationship = {
  id: number;
  type: FirmRelationshipType;
  other_firm: FirmRelationshipOtherFirm;
};

export type FirmRelationshipsResponse = {
  outgoing: FirmRelationship[];
  incoming: FirmRelationship[];
  requestId: string | null;
};

export type FirmRelationshipCreateRequest = {
  target_id: string;
  type: FirmRelationshipType;
};

// ============================================================================
// Relationships Service
// ============================================================================

export const relationships = {
  getAll: async (companyId: string): Promise<FirmRelationshipsResponse> => {
    return api.get<FirmRelationshipsResponse>(
      `/v1/address-book/companies/${companyId}/relationships`,
    );
  },

  create: async (
    companyId: string,
    data: FirmRelationshipCreateRequest,
  ): Promise<{ relationship: unknown; requestId: string | null }> => {
    return api.post(`/v1/address-book/companies/${companyId}/relationships`, data);
  },

  delete: async (companyId: string, relationshipId: number): Promise<void> => {
    await api.del(`/v1/address-book/companies/${companyId}/relationships/${relationshipId}`);
  },
};

// ============================================================================
// External References Service
// ============================================================================

export type ExternalReferenceEntityType = "company" | "contact";

const EXTERNAL_REF_PATH_SEGMENT: Record<ExternalReferenceEntityType, string> = {
  company: "companies",
  contact: "contacts",
};

export const externalReferences = {
  getByEntity: async (
    entityType: string,
    entityId: string,
  ): Promise<PaginatedResponse<ExternalReference>> => {
    const params = new URLSearchParams();
    params.append("entityType", entityType);
    params.append("entityId", entityId);
    const response = await api.get<{
      externalReferences: ExternalReference[];
      pagination: PaginatedResponse<ExternalReference>["pagination"];
      requestId: string | null;
    }>(`/v1/address-book/external-references?${params.toString()}`);
    return {
      data: response.externalReferences,
      pagination: response.pagination,
      requestId: response.requestId,
    };
  },

  create: async (
    entityType: ExternalReferenceEntityType,
    entityId: string,
    body: { reference_type: string; reference_value: string },
  ): Promise<SingleResponse<ExternalReference>> => {
    const response = await api.post<{
      externalReference: ExternalReference;
      requestId: string | null;
    }>(
      `/v1/address-book/${EXTERNAL_REF_PATH_SEGMENT[entityType]}/${entityId}/external-references`,
      body,
    );
    return { data: response.externalReference, requestId: response.requestId };
  },

  delete: async (
    entityType: ExternalReferenceEntityType,
    entityId: string,
    referenceId: string,
  ): Promise<void> => {
    await api.del(
      `/v1/address-book/${EXTERNAL_REF_PATH_SEGMENT[entityType]}/${entityId}/external-references/${referenceId}`,
    );
  },

  update: async (
    entityType: ExternalReferenceEntityType,
    entityId: string,
    referenceId: string,
    body: { reference_value?: string; is_canonical?: boolean },
  ): Promise<SingleResponse<ExternalReference>> => {
    const response = await api.patch<{
      externalReference: ExternalReference;
      requestId: string | null;
    }>(
      `/v1/address-book/${EXTERNAL_REF_PATH_SEGMENT[entityType]}/${entityId}/external-references/${referenceId}`,
      body,
    );
    return { data: response.externalReference, requestId: response.requestId };
  },
};

// ============================================================================
// Bulk Reference Runs
// ============================================================================

/** One row's recorded outcome. Snake_case because it is stored and returned verbatim. */
export type BulkRunRow = {
  line_number: number;
  company_uuid: string;
  company_name: string;
  reference_value: string;
  outcome: string;
  http_status: number | null;
  detail: string;
};

export type BulkReferenceRun = {
  id: string;
  referenceType: string;
  fileName: string | null;
  status: "running" | "completed" | "abandoned";
  startedByEmail: string | null;
  totalRows: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  startedAt: string;
  finishedAt: string | null;
};

export type BulkReferenceRunDetail = BulkReferenceRun & { rows: BulkRunRow[] };

const BULK_RUNS_BASE = "/v1/address-book/bulk-reference-runs";

/**
 * Reports on bulk external-reference uploads. The upload happens in the browser, so
 * these calls record what it did rather than starting anything: open a run, checkpoint
 * as it goes, finalise at the end.
 */
// ============================================================================
// Search (for EntitySearchPicker / Add Target)
// ============================================================================

export type SearchCompanyResult = {
  id: string;
  name: string;
  contentId: number | null;
  firmRef: string | null;
  offices?: Array<{
    officeId: number;
    city: string | null;
    countryName: string | null;
    countryId: number | null;
  }>;
};

export type SearchCompaniesResponse = {
  companies: SearchCompanyResult[];
  pagination: { total: number; limit: number; offset: number; hasMore: boolean };
};

export type RefPickerContactItem = {
  term_id: string;
  office_term_id: string | null;
  contact_address_book_id: string;
  firm_address_book_id: string;
  display: string;
  insert: string;
  entity_type: string;
  parent_type: string;
  parent_id: string;
  office_id: string | null;
};

export type SearchContactsResponse = {
  items: RefPickerContactItem[];
  nextCursor: string | null;
};

export const search = {
  companies: async (query: string, limit = 100, offset = 0): Promise<SearchCompaniesResponse> => {
    const params = new URLSearchParams({
      q: query,
      limit: String(limit),
      offset: String(offset),
      includeOffices: "true",
    });
    return api.get<SearchCompaniesResponse>(`/v1/address-book/search/companies?${params}`);
  },

  contacts: async (query: string, limit = 20): Promise<SearchContactsResponse> => {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return api.get<SearchContactsResponse>(`/v1/address-book/search/contacts?${params}`);
  },
};

// ============================================================================
// Bulk Reference Runs
// ============================================================================

export const bulkReferenceRuns = {
  open: async (body: {
    reference_type: string;
    file_name: string | null;
    total_rows: number;
  }): Promise<SingleResponse<BulkReferenceRun>> => {
    const response = await api.post<{ run: BulkReferenceRun; requestId: string | null }>(
      BULK_RUNS_BASE,
      body,
    );
    return { data: response.run, requestId: response.requestId };
  },

  /** Omit `status` to checkpoint; pass "completed" to finalise. */
  report: async (
    id: string,
    body: {
      status?: "completed" | "abandoned";
      created_count: number;
      skipped_count: number;
      failed_count: number;
      rows: BulkRunRow[];
    },
  ): Promise<SingleResponse<BulkReferenceRun>> => {
    const response = await api.patch<{ run: BulkReferenceRun; requestId: string | null }>(
      `${BULK_RUNS_BASE}/${id}`,
      body,
    );
    return { data: response.run, requestId: response.requestId };
  },

  list: async (limit = 25, offset = 0): Promise<PaginatedResponse<BulkReferenceRun>> => {
    const response = await api.get<{
      runs: BulkReferenceRun[];
      pagination: PaginatedResponse<BulkReferenceRun>["pagination"];
      requestId: string | null;
    }>(`${BULK_RUNS_BASE}?limit=${limit}&offset=${offset}`);
    return {
      data: response.runs,
      pagination: response.pagination,
      requestId: response.requestId,
    };
  },

  get: async (id: string): Promise<SingleResponse<BulkReferenceRunDetail>> => {
    const response = await api.get<{
      run: BulkReferenceRunDetail;
      requestId: string | null;
    }>(`${BULK_RUNS_BASE}/${id}`);
    return { data: response.run, requestId: response.requestId };
  },
};
